<template>
    <div>
        <div id="navigationPlaceholder" style="padding-top: 80px;">
            <AdminNavigationV2 :tid="tid"></AdminNavigationV2>
        </div>

        <div class="container">
            <div class="row">
                <div class="col-lg-7">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b;">Configure IDs</h1>

                    <a :href="`/#/admin/integrations/pipelines/details/${tid}/${id}`" type="button" class="btn btn-link btn-sm">
                        <i class="fa fa-arrow-left" aria-hidden="true"></i>
                        <span>Back</span>
                    </a>
                </div>
                <div class="col-lg-5 col-sm-6 text-right">
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 30px;" />

        <div class="container">
            <div class="row">
                <div class="col-5 text-right" :class="{ 'order-1': direction==1, 'order-3': direction==2 }">
                    <h5>Connector</h5>
                </div>

                <div class="col-2 order-2 text-center">
                    <i v-if="direction==1" class="fa fa-arrow-circle-right fa-2x" title="From connector to dataset"></i>
                    <i v-if="direction==2" class="fa fa-arrow-circle-right fa-2x" title="From dataset to connector"></i>
                </div>

                <div class="col-5" :class="{ 'order-1': direction==2, 'order-3': direction==1 }">
                    <h5>Dataset</h5>
                </div>
            </div>

            <div class="row">
                <div class="col-5 text-right" :class="{ 'order-1': direction==1, 'order-3': direction==2 }">
                    <div v-for="(idPropertyNameForConnector, idx) in idPropertyNamesForConnector" :key="idx" class="input-group mb-1">
                        <div class="input-group-prepend">
                            <span class="input-group-text">ID{{idx+1}}</span>
                        </div>
                        <input v-model.trim="idPropertyNamesForConnector[idx]" type="text" class="form-control">
                        <div class="input-group-append">
                            <button @click="handleRemoveIdPropertyNameForConnector(idx)" class="btn btn-danger btn-sm">
                                <i class="fa-regular fa-trash-can"></i>
                            </button>
                        </div>
                    </div>
                    <button @click="handleAddIdPropertyNameForConnector" class="btn btn-sm">Add</button>
                    <button @click="invokeChangeIdPropertyNamesForConnectorCommandV1" class="btn btn-outline-secondary" type="button">Save</button>
                </div>

                <div class="col-2 order-2 text-center">
                </div>

                <div class="col-5" :class="{ 'order-1': direction==2, 'order-3': direction==1 }">
                    <div v-for="(idPropertyNameForDataset, idx) in idPropertyNamesForDataset" :key="idx" class="input-group mb-1">
                        <div class="input-group-prepend">
                            <span class="input-group-text">ID{{idx+1}}</span>
                        </div>
                        <input v-model.trim="idPropertyNamesForDataset[idx]" type="text" class="form-control">
                        <div class="input-group-append">
                            <button @click="handleRemoveIdPropertyNameForDataset(idx)" class="btn btn-danger btn-sm">
                                <i class="fa-regular fa-trash-can"></i>
                            </button>
                        </div>
                    </div>
                    <button @click="handleAddIdPropertyNameForDataset" class="btn btn-sm">Add</button>
                    <button @click="invokeChangeIdPropertyNamesForDatasetCommandV1" class="btn btn-outline-secondary" type="button">Save</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
    import { useId, useOrganizationUnitId } from '@common/composables'
    import { useAdminConfigurePipelineIdsPage } from '@Integrations/Pipelines/pages-core'

    document.title = "Configure IDs"

    const tid = useOrganizationUnitId()
    const id = useId()

    const connectorId = ref()

    const idPropertyNameForConnector = ref()
    const idPropertyNameForDataset = ref()

    const idPropertyNamesForConnector = ref([])
    const idPropertyNamesForDataset = ref([])

    const nameForConnector = ref()
    const nameForDataset = ref()

    const {
        useCurrentUserQuery,
        usePipelineDetailsQuery,
        useConnectorPropertyNamesQuery,
        useChangeIdPropertyNamesForConnectorCommand,
        useChangeIdPropertyNamesForDatasetCommand
    } = useAdminConfigurePipelineIdsPage()

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)

    const { runPipelineDetailsQueryV1, modelPipelineDetailsQueryV1 } = usePipelineDetailsQuery(id)
    const direction = computed(() => modelPipelineDetailsQueryV1.value?.Direction)
    const mappings = computed(() => modelPipelineDetailsQueryV1.value?.Mappings || [])

    const { dataStream, runConnectorPropertyNamesQueryV1, modelConnectorPropertyNamesQueryV1 } = useConnectorPropertyNamesQuery(connectorId)
    const propertyNames = computed(() => modelConnectorPropertyNamesQueryV1.value?.PropertyNames || [])

    const { invokeChangeIdPropertyNamesForConnectorCommandV1, succeedChangeIdPropertyNamesForConnectorCommandV1 } = useChangeIdPropertyNamesForConnectorCommand(id, tid, userId, idPropertyNamesForConnector)
    const { invokeChangeIdPropertyNamesForDatasetCommandV1, succeedChangeIdPropertyNamesForDatasetCommandV1 } = useChangeIdPropertyNamesForDatasetCommand(id, tid, userId, idPropertyNamesForDataset)

    function handleAddPropertyNameMappingCommand() {
        invokeAddPropertyNameMappingCommandV1()
    }

    function handleRemovePropertyNameMappingCommandV1(mapping: any) {
        invokeRemovePropertyNameMappingCommandV1({
            nameForConnector: mapping.NameForConnector,
            nameForDataset: mapping.NameForDataset,
        })
    }

    function handleAddIdPropertyNameForConnector() {
        idPropertyNamesForConnector.value.push("")
    }

    function handleRemoveIdPropertyNameForConnector(index: number) {
        idPropertyNamesForConnector.value.splice(index, 1)
    }

    function handleAddIdPropertyNameForDataset() {
        idPropertyNamesForDataset.value.push("")
    }

    function handleRemoveIdPropertyNameForDataset(index: number) {
        idPropertyNamesForDataset.value.splice(index, 1)
    }

    watch(modelPipelineDetailsQueryV1, () => {
        connectorId.value = modelPipelineDetailsQueryV1.value.ConnectorId
        dataStream.value = modelPipelineDetailsQueryV1.value.DataStream
        idPropertyNameForConnector.value = modelPipelineDetailsQueryV1.value.IdPropertyNameForConnector ?? 'Id'
        idPropertyNameForDataset.value = modelPipelineDetailsQueryV1.value.IdPropertyNameForDataset ?? 'Id'
        idPropertyNamesForConnector.value = modelPipelineDetailsQueryV1.value.IdPropertyNamesForConnector ?? ['Id']
        idPropertyNamesForDataset.value = modelPipelineDetailsQueryV1.value.IdPropertyNamesForDataset ?? ['Id']
    })

    watch(connectorId, () => runConnectorPropertyNamesQueryV1())

    onMounted(() => {
        runCurrentUserQueryV1()
        runPipelineDetailsQueryV1()
    })
</script>